import { Property } from "../../../contexts/properties/PropertiesContext";
import { TSummaryDataAverageOccupancy, TSummaryDataExpiringCounts, TSummaryDataFinancialMetric } from "../logic/logic";
import { arraySum } from "../../../utils/array";
import { COLORS } from "../../../constants/Colors";
import { formatterDollarUSNoDecimal, formatterInt, formatterPercentAutoSign } from "../../../utils/formatters";
import { CellValue } from "handsontable/common";
import { getHotTableDataFromParsedSeriesData } from "../../planning-dashboard/data/logic/operationalData";
import { VersionType } from "../../../__generated__/generated_types";
import { propertyDisplayBudgetYear } from "../../../contexts/properties/logic";
import { getMonths } from "../../../utils/months-helper";

export type ExecutiveSummaryGraphData = {
    dataType: 'dollar' | 'percent' | 'count',
    varianceCounts: (number | null)[],
    chartSeriesData: {
        year: number,
        versionType: VersionType,
        values: (number | null)[],
        counts: number[],
        name: string,
        color: string,
        type: string,
        averageValue: number,
        averageCount: number,
        totalValue: number,
        totalCount: number,
    }[],
    summary: {
        comparisonItemOne: {
            label: string,
            value: number | string,
        },
        comparisonItemTwo: {
            label: string,
            value: number | string,
        },
        variancePercent: {
            label: string,
            value: number | string,
        },
        varianceAmount: {
            label: string,
            value: number | string,
        }
    },
    tableData: undefined | {
        colHeaders: string[],
        data: CellValue[][]
    }
}

export function buildGraphSeriesDataOccupancy(occupancyStat: TSummaryDataAverageOccupancy, property: Property): ExecutiveSummaryGraphData {
    const varianceCounts = arraySum(occupancyStat.budgetOccupiedCountMonthly, occupancyStat.reforecastOccupiedCountMonthly, -1);
    const chartSeriesData = [
        {
            year: property.reforecastYear,
            versionType: VersionType.Actuals,
            name: `${property.reforecastYear % 100} REFORECAST - ACTUAL`,
            color: COLORS.LAVENDER_400,
            type: "bar",
            values: occupancyStat.reforecastOccupancy
                .slice(0, property.reforecastStartMonthIndex)
                .map(v => +((v * 100).toFixed(1)))
                .concat(new Array(12 - property.reforecastStartMonthIndex).fill(null)),
            counts: occupancyStat.reforecastOccupiedCountMonthly,
            averageValue: +((occupancyStat.reforecastOccupancy.average() * 100).toFixed(1)),
            averageCount: occupancyStat.reforecastOccupiedCountMonthly.average(),
            totalValue: +((occupancyStat.reforecastOccupancy.average() * 100).toFixed(1)),
            totalCount: occupancyStat.reforecastOccupiedCountMonthly.sum()
        },
        {
            year: property.reforecastYear,
            versionType: VersionType.Reforecast,
            name: `${property.reforecastYear % 100} REFORECAST - FORECAST`,
            color: COLORS.LAVENDER_200,
            type: "bar",
            values: new Array(property.reforecastStartMonthIndex).fill(null)
                .concat(occupancyStat.reforecastOccupancy
                    .slice(property.reforecastStartMonthIndex, 12)
                    .map(v => +((v * 100).toFixed(1)))
                ),
            counts: occupancyStat.reforecastOccupiedCountMonthly,
            averageValue: +((occupancyStat.reforecastOccupancy.average() * 100).toFixed(1)),
            averageCount: occupancyStat.reforecastOccupiedCountMonthly.average(),
            totalValue: occupancyStat.reforecastOccupancy.average() * 100,
            totalCount: occupancyStat.reforecastOccupiedCountMonthly.sum()
        },
        {
            year: property.budgetYear,
            versionType: VersionType.Budget,
            name: `${propertyDisplayBudgetYear(property) % 100} BUDGET`,
            color: COLORS.PRIMARY_400,
            type: "line",
            values: occupancyStat.budgetOccupancy.map(v => +((v * 100).toFixed(1))),
            counts: occupancyStat.budgetOccupiedCountMonthly,
            averageValue: +((occupancyStat.budgetOccupancy.average() * 100).toFixed(1)),
            averageCount: occupancyStat.budgetOccupiedCountMonthly.average(),
            totalValue: +((occupancyStat.budgetOccupancy.average() * 100).toFixed(1)),
            totalCount: occupancyStat.budgetOccupiedCountMonthly.sum()
        },
    ];
    const summary = {
        comparisonItemOne: {
            label: `${property.reforecastYear % 100} RFCST`,
            value: occupancyStat.reforecastAvgOccupancy === null ? "-" : formatterPercentAutoSign.format(occupancyStat.reforecastAvgOccupancy)
        },
        comparisonItemTwo: {
            label: `${propertyDisplayBudgetYear(property) % 100} BDGT`,
            value: occupancyStat.budgetAvgOccupancy === null ? "-" : formatterPercentAutoSign.format(occupancyStat.budgetAvgOccupancy)
        },
        variancePercent: {
            label: "% Change",
            value: occupancyStat.reforecastAvgOccupancy === null
                    && occupancyStat.budgetAvgOccupancy === null
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    ? "-" : formatterPercentAutoSign.format(occupancyStat.budgetAvgOccupancy! - occupancyStat.reforecastAvgOccupancy!)
        },
        varianceAmount: {
            label: "# Change",
            value: varianceCounts.average().toFixed(0)
        }
    };
    const tableData = getHotTableDataFromParsedSeriesData(
        {varianceCounts, chartSeriesData},
        summary,
        "percent",
        getMonths(property)
    );
    return {
        dataType: "percent",
        varianceCounts: varianceCounts,
        chartSeriesData: chartSeriesData,
        summary: summary,
        tableData
    };
}

export function buildGraphSeriesDataExpirations(expiringCountsStat: TSummaryDataExpiringCounts, property: Property): ExecutiveSummaryGraphData {
    const varianceCounts = arraySum(expiringCountsStat.budgetExpirationsMonthly, expiringCountsStat.reforecastExpirationsMonthly, -1);
    const chartSeriesData = [
        {
            year: property.reforecastYear,
            versionType: VersionType.Actuals,
            name: `${property.reforecastYear % 100} REFORECAST - ACTUAL`,
            color: COLORS.LAVENDER_400,
            type: "bar",
            values: expiringCountsStat.reforecastExpirationsMonthly
                .slice(0, property.reforecastStartMonthIndex)
                .map(v => +v.toFixed())
                .concat(new Array(12 - property.reforecastStartMonthIndex).fill(null)),
            counts: expiringCountsStat.reforecastExpirationsMonthly.map(v => +v.toFixed()),
            averageValue: +((expiringCountsStat.reforecastExpirationsMonthly.average()).toFixed()),
            averageCount: +((expiringCountsStat.reforecastExpirationsMonthly.average()).toFixed()),
            totalValue: +((expiringCountsStat.reforecastExpirations ?? 0).toFixed()),
            totalCount: +((expiringCountsStat.reforecastExpirations ?? 0).toFixed()),
        },
        {
            year: property.reforecastYear,
            versionType: VersionType.Reforecast,
            name: `${property.reforecastYear % 100} REFORECAST - FORECAST`,
            color: COLORS.LAVENDER_200,
            type: "bar",
            values: new Array(property.reforecastStartMonthIndex).fill(null)
                .concat(expiringCountsStat.reforecastExpirationsMonthly
                    .slice(property.reforecastStartMonthIndex, 12)
                    .map(v => v.toFixed())
                ),
            counts: expiringCountsStat.reforecastExpirationsMonthly,
            averageValue: +((expiringCountsStat.reforecastExpirationsMonthly.average()).toFixed()),
            averageCount: +((expiringCountsStat.reforecastExpirationsMonthly.average()).toFixed()),
            totalValue: +((expiringCountsStat.reforecastExpirations ?? 0).toFixed()),
            totalCount: +((expiringCountsStat.reforecastExpirations ?? 0).toFixed()),
        },
        {
            year: property.budgetYear,
            versionType: VersionType.Budget,
            name: `${propertyDisplayBudgetYear(property) % 100} BUDGET`,
            color: COLORS.PRIMARY_400,
            type: "line",
            values: expiringCountsStat.budgetExpirationsMonthly.map(v => +v.toFixed()),
            counts: expiringCountsStat.budgetExpirationsMonthly.map(v => +v.toFixed()),
            averageValue: +((expiringCountsStat.budgetExpirationsMonthly.average()).toFixed()),
            averageCount: +((expiringCountsStat.budgetExpirationsMonthly.average()).toFixed()),
            totalValue: +((expiringCountsStat.budgetExpirations ?? 0).toFixed()),
            totalCount: +((expiringCountsStat.budgetExpirations ?? 0).toFixed()),
        },
    ];
    const summary = {
        comparisonItemOne: {
            label: `${property.reforecastYear % 100} RFCST`,
            value: expiringCountsStat.reforecastExpirations === null ? "-" : formatterInt.format(expiringCountsStat.reforecastExpirations)
        },
        comparisonItemTwo: {
            label: `${propertyDisplayBudgetYear(property) % 100} BDGT`,
            value: expiringCountsStat.budgetExpirations === null ? "-" : formatterInt.format(expiringCountsStat.budgetExpirations)
        },
        variancePercent: {
            label: "% Change",
            value: expiringCountsStat.expitationsVariancePercent === null
                    ? "-" : formatterPercentAutoSign.format(expiringCountsStat.expitationsVariancePercent)
        },
        varianceAmount: {
            label: "# Change",
            value: expiringCountsStat.expirationsVarianceAmount === null ? "-" : formatterInt.format(expiringCountsStat.expirationsVarianceAmount)
        }
    };
    const tableData = getHotTableDataFromParsedSeriesData(
        {varianceCounts, chartSeriesData},
        summary,
        "count",
        getMonths(property)
    );
    return {
        dataType: "count",
        varianceCounts: varianceCounts,
        chartSeriesData: chartSeriesData,
        summary: summary,
        tableData
    };
}

export function buildGraphSeriesDataRenewals(expiringCountsStat: TSummaryDataExpiringCounts, property: Property): ExecutiveSummaryGraphData {
    const varianceCounts = arraySum(expiringCountsStat.budgetExpiringRenewalsMonthly, expiringCountsStat.reforecastExpiringRenewalsMonthly, -1);
    const chartSeriesData = [
        {
            year: property.reforecastYear,
            versionType: VersionType.Actuals,
            name: `${property.reforecastYear % 100} REFORECAST - ACTUAL`,
            color: COLORS.LAVENDER_400,
            type: "bar",
            values: expiringCountsStat.reforecastExpiringRenewalsMonthly
                .slice(0, property.reforecastStartMonthIndex)
                .map(v => +(v.toFixed(1)))
                .concat(new Array(12 - property.reforecastStartMonthIndex).fill(null)),
            counts: expiringCountsStat.reforecastExpiringRenewalsMonthly,
            averageValue: expiringCountsStat.reforecastExpiringRenewalsMonthly.average(),
            averageCount: expiringCountsStat.reforecastExpiringRenewalsMonthly.average(),
            totalValue: expiringCountsStat.reforecastExpiringRenewals ?? 0,
            totalCount: expiringCountsStat.reforecastExpiringRenewals ?? 0
        },
        {
            year: property.reforecastYear,
            versionType: VersionType.Reforecast,
            name: `${property.reforecastYear % 100} REFORECAST - FORECAST`,
            color: COLORS.LAVENDER_200,
            type: "bar",
            values: new Array(property.reforecastStartMonthIndex).fill(null)
                .concat(expiringCountsStat.reforecastExpiringRenewalsMonthly
                    .slice(property.reforecastStartMonthIndex, 12)
                    .map(v => +(v.toFixed(1)))
                ),
            counts: expiringCountsStat.reforecastExpiringRenewalsMonthly,
            averageValue: expiringCountsStat.reforecastExpiringRenewalsMonthly.average(),
            averageCount: expiringCountsStat.reforecastExpiringRenewalsMonthly.average(),
            totalValue: expiringCountsStat.reforecastExpiringRenewals ?? 0,
            totalCount: expiringCountsStat.reforecastExpiringRenewals ?? 0
        },
        {
            year: property.budgetYear,
            versionType: VersionType.Budget,
            name: `${propertyDisplayBudgetYear(property) % 100} BUDGET`,
            color: COLORS.PRIMARY_400,
            type: "line",
            values: expiringCountsStat.budgetExpiringRenewalsMonthly
                        .map(v => +(v.toFixed(1))),
            counts: expiringCountsStat.budgetExpiringRenewalsMonthly,
            averageValue: expiringCountsStat.budgetExpiringRenewalsMonthly.average(),
            averageCount: expiringCountsStat.budgetExpiringRenewalsMonthly.average(),
            totalValue: expiringCountsStat.budgetExpiringRenewals ?? 0,
            totalCount: expiringCountsStat.budgetExpiringRenewals ?? 0
        },
    ];
    const summary = {
        comparisonItemOne: {
            label: `${property.reforecastYear % 100} RFCST`,
            value: expiringCountsStat.reforecastExpiringRenewals === null ? "-" : expiringCountsStat.reforecastExpiringRenewals.toFixed(1)
        },
        comparisonItemTwo: {
            label: `${propertyDisplayBudgetYear(property) % 100} BDGT`,
            value: expiringCountsStat.budgetExpiringRenewals === null ? "-" : expiringCountsStat.budgetExpiringRenewals.toFixed(1)
        },
        variancePercent: {
            label: "% Change",
            value: expiringCountsStat.expiringRenewalsVariancePercent === null
                    ? "-" : formatterPercentAutoSign.format(expiringCountsStat.expiringRenewalsVariancePercent)
        },
        varianceAmount: {
            label: "# Change",
            value: expiringCountsStat.expiringRenewalsVarianceAmount === null ? "-" : expiringCountsStat.expiringRenewalsVarianceAmount.toFixed(1)
        }
    };
    const tableData = getHotTableDataFromParsedSeriesData(
        {varianceCounts, chartSeriesData},
        summary,
        "count",
        getMonths(property)
    );
    return {
        dataType: "count",
        varianceCounts: varianceCounts,
        chartSeriesData: chartSeriesData,
        summary: summary,
        tableData
    };
}

export function buildGraphSeriesDataMoveOuts(expiringCountsStat: TSummaryDataExpiringCounts, property: Property): ExecutiveSummaryGraphData {
    const varianceCounts = arraySum(expiringCountsStat.budgetExpiringMoveOutsMonthly, expiringCountsStat.reforecastExpiringMoveOutsMonthly, -1);
    const chartSeriesData = [
        {
            year: property.reforecastYear,
            versionType: VersionType.Actuals,
            name: `${property.reforecastYear % 100} REFORECAST - ACTUAL`,
            color: COLORS.LAVENDER_400,
            type: "bar",
            values: expiringCountsStat.reforecastExpiringMoveOutsMonthly
                .slice(0, property.reforecastStartMonthIndex)
                .map(v => +(v.toFixed(1)))
                .concat(new Array(12 - property.reforecastStartMonthIndex).fill(null)),
            counts: expiringCountsStat.reforecastExpiringMoveOutsMonthly,
            averageValue: +((expiringCountsStat.reforecastExpiringMoveOutsMonthly.average()).toFixed(1)),
            averageCount: +((expiringCountsStat.reforecastExpiringMoveOutsMonthly.average()).toFixed(1)),
            totalValue: +((expiringCountsStat.reforecastExpiringMoveOuts ?? 0).toFixed(1)),
            totalCount: +((expiringCountsStat.reforecastExpiringMoveOuts ?? 0).toFixed(1))
        },
        {
            year: property.reforecastYear,
            versionType: VersionType.Reforecast,
            name: `${property.reforecastYear % 100} REFORECAST - FORECAST`,
            color: COLORS.LAVENDER_200,
            type: "bar",
            values: new Array(property.reforecastStartMonthIndex).fill(null)
                .concat(expiringCountsStat.reforecastExpiringMoveOutsMonthly
                    .slice(property.reforecastStartMonthIndex, 12)
                     .map(v => +(v.toFixed(1)))
                 ),
            counts: expiringCountsStat.reforecastExpiringMoveOutsMonthly,
            averageValue: +((expiringCountsStat.reforecastExpiringMoveOutsMonthly.average()).toFixed(1)),
            averageCount: +((expiringCountsStat.reforecastExpiringMoveOutsMonthly.average()).toFixed(1)),
            totalValue: +((expiringCountsStat.reforecastExpiringMoveOuts ?? 0).toFixed(1)),
            totalCount: +((expiringCountsStat.reforecastExpiringMoveOuts ?? 0).toFixed(1))
        },
        {
            year: property.budgetYear,
            versionType: VersionType.Budget,
            name: `${propertyDisplayBudgetYear(property) % 100} BUDGET`,
            color: COLORS.PRIMARY_400,
            type: "line",
            values: expiringCountsStat.budgetExpiringMoveOutsMonthly.map(v => +(v.toFixed(1))),
            counts: expiringCountsStat.budgetExpiringMoveOutsMonthly.map(v => +(v.toFixed(1))),
            averageValue: +((expiringCountsStat.budgetExpiringMoveOutsMonthly.average()).toFixed(1)),
            averageCount: +((expiringCountsStat.budgetExpiringMoveOutsMonthly.average()).toFixed(1)),
            totalValue: +((expiringCountsStat.budgetExpiringMoveOuts ?? 0).toFixed(1)),
            totalCount: +((expiringCountsStat.budgetExpiringMoveOuts ?? 0).toFixed(1))
        },
    ];
    const summary = {
        comparisonItemOne: {
            label: `${property.reforecastYear % 100} RFCST`,
            value: expiringCountsStat.reforecastExpiringMoveOuts === null ? "-" : expiringCountsStat.reforecastExpiringMoveOuts.toFixed(1)
        },
        comparisonItemTwo: {
            label: `${propertyDisplayBudgetYear(property) % 100} BDGT`,
            value: expiringCountsStat.budgetExpiringMoveOuts === null ? "-" : expiringCountsStat.budgetExpiringMoveOuts.toFixed(1)
        },
        variancePercent: {
            label: "% Change",
            value: expiringCountsStat.expiringMoveOutsVariancePercent === null
                    ? "-" : formatterPercentAutoSign.format(expiringCountsStat.expiringMoveOutsVariancePercent)
        },
        varianceAmount: {
            label: "# Change",
            value: expiringCountsStat.expiringMoveOutsVarianceAmount === null ? "-" : expiringCountsStat.expiringMoveOutsVarianceAmount.toFixed(1)
        }
    };
    const tableData = getHotTableDataFromParsedSeriesData(
        {varianceCounts, chartSeriesData},
        summary,
        "count",
        getMonths(property)
    );
    return {
        dataType: "count",
        varianceCounts: varianceCounts,
        chartSeriesData: chartSeriesData,
        summary: summary,
        tableData
    };
}

export function buildGraphSeriesDataMoveOutRatios(expiringCountsStat: TSummaryDataExpiringCounts, property: Property): ExecutiveSummaryGraphData {
    const varianceCounts = arraySum(expiringCountsStat.budgetExpiringMoveOutsMonthly, expiringCountsStat.reforecastExpiringMoveOutsMonthly, -1);
    const chartSeriesData = [
        {
            year: property.reforecastYear,
            versionType: VersionType.Actuals,
            name: `${property.reforecastYear % 100} REFORECAST - ACTUAL`,
            color: COLORS.LAVENDER_400,
            type: "bar",
            values: expiringCountsStat.reforecastExpiringMoveOutRatiosMonthly
                .slice(0, property.reforecastStartMonthIndex)
                .map(v => +((v * 100).toFixed(1)))
                .concat(new Array(12 - property.reforecastStartMonthIndex).fill(null)),
            counts: expiringCountsStat.reforecastExpiringMoveOutsMonthly.map(v => +(v.toFixed(1))),
            averageValue: +((expiringCountsStat.reforecastExpiringMoveOutRatiosMonthly.average() * 100).toFixed(1)),
            averageCount: +((expiringCountsStat.reforecastExpiringMoveOutsMonthly.average()).toFixed(1)),
            totalValue: +((expiringCountsStat.reforecastExpiringMoveOutRatiosMonthly.average() * 100).toFixed(1)),
            totalCount: +((expiringCountsStat.reforecastExpiringMoveOutsMonthly.sum()).toFixed(1)),
        },
        {
            year: property.reforecastYear,
            versionType: VersionType.Reforecast,
            name: `${property.reforecastYear % 100} REFORECAST - FORECAST`,
            color: COLORS.LAVENDER_200,
            type: "bar",
            values: new Array(property.reforecastStartMonthIndex).fill(null)
                .concat(expiringCountsStat.reforecastExpiringMoveOutRatiosMonthly
                    .slice(property.reforecastStartMonthIndex, 12)
                    .map(v => +((v * 100).toFixed(1)))
                ),
            counts: expiringCountsStat.reforecastExpiringMoveOutsMonthly.map(v => +(v.toFixed(1))),
            averageValue: +((expiringCountsStat.reforecastExpiringMoveOutRatiosMonthly.average() * 100).toFixed(1)),
            averageCount: +((expiringCountsStat.reforecastExpiringMoveOutsMonthly.average()).toFixed(1)),
            totalValue: +((expiringCountsStat.reforecastExpiringMoveOutRatiosMonthly.average() * 100).toFixed(1)),
            totalCount: +((expiringCountsStat.reforecastExpiringMoveOutsMonthly.sum()).toFixed(1)),
        },
        {
            year: property.budgetYear,
            versionType: VersionType.Budget,
            name: `${propertyDisplayBudgetYear(property) % 100} BUDGET`,
            color: COLORS.PRIMARY_400,
            type: "line",
            values: expiringCountsStat.budgetExpiringMoveOutRatiosMonthly.map(v => +((v * 100).toFixed(1))),
            counts: expiringCountsStat.budgetExpiringMoveOutsMonthly.map(v => +(v.toFixed(1))),
            averageValue: +((expiringCountsStat.budgetExpiringMoveOutRatiosMonthly.average() * 100).toFixed(1)),
            averageCount: +((expiringCountsStat.budgetExpiringMoveOutsMonthly.average()).toFixed(1)),
            totalValue: +((expiringCountsStat.budgetExpiringMoveOutRatiosMonthly.average() * 100).toFixed(1)),
            totalCount: +((expiringCountsStat.budgetExpiringMoveOutsMonthly.sum()).toFixed(1)),
        },
    ];
    const summary = {
        comparisonItemOne: {
            label: `${property.reforecastYear % 100} RFCST`,
            value: expiringCountsStat.reforecastAvgExpiringMoveOutRatio === null ? "-" : formatterPercentAutoSign.format(expiringCountsStat.reforecastAvgExpiringMoveOutRatio)
        },
        comparisonItemTwo: {
            label: `${propertyDisplayBudgetYear(property) % 100} BDGT`,
            value: expiringCountsStat.budgetAvgExpiringMoveOutRatio === null ? "-" : formatterPercentAutoSign.format(expiringCountsStat.budgetAvgExpiringMoveOutRatio)
        },
        variancePercent: {
            label: "% Change",
            value: expiringCountsStat.reforecastAvgExpiringMoveOutRatio === null
                    && expiringCountsStat.budgetAvgExpiringMoveOutRatio === null
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    ? "-" : formatterPercentAutoSign.format(expiringCountsStat.budgetAvgExpiringMoveOutRatio! - expiringCountsStat.reforecastAvgExpiringMoveOutRatio!)
        },
        varianceAmount: {
            label: "# Change",
            value: varianceCounts.average().toFixed(0)
        }
    };
    const tableData = getHotTableDataFromParsedSeriesData(
        {varianceCounts, chartSeriesData},
        summary,
        "percent",
        getMonths(property)
    );
    return {
        dataType: "percent",
        varianceCounts: varianceCounts,
        chartSeriesData: chartSeriesData,
        summary: summary,
        tableData
    };
}

export function buildGraphSeriesDataRenewalRatios(expiringCountsStat: TSummaryDataExpiringCounts, property: Property): ExecutiveSummaryGraphData {
    const varianceCounts = arraySum(expiringCountsStat.budgetExpiringRenewalsMonthly, expiringCountsStat.reforecastExpiringRenewalsMonthly, -1);
    const chartSeriesData = [
        {
            year: property.reforecastYear,
            versionType: VersionType.Actuals,
            name: `${property.reforecastYear % 100} REFORECAST - ACTUAL`,
            color: COLORS.LAVENDER_400,
            type: "bar",
            values: expiringCountsStat.reforecastExpiringRenewalRatiosMonthly
                .slice(0, property.reforecastStartMonthIndex)
                .map(v => +((v * 100).toFixed(1)))
                .concat(new Array(12 - property.reforecastStartMonthIndex).fill(null)),
            counts: expiringCountsStat.reforecastExpiringRenewalsMonthly,
            averageValue: +((expiringCountsStat.reforecastExpiringRenewalRatiosMonthly.average() * 100).toFixed(1)),
            averageCount: +((expiringCountsStat.reforecastExpiringRenewalsMonthly.average()).toFixed(1)),
            totalValue: +((expiringCountsStat.reforecastExpiringRenewalRatiosMonthly.average() * 100).toFixed(1)),
            totalCount: +((expiringCountsStat.reforecastExpiringRenewalsMonthly.sum()).toFixed(1)),
        },
        {
            year: property.reforecastYear,
            versionType: VersionType.Reforecast,
            name: `${property.reforecastYear % 100} REFORECAST - FORECAST`,
            color: COLORS.LAVENDER_200,
            type: "bar",
            values: new Array(property.reforecastStartMonthIndex).fill(null)
                .concat(expiringCountsStat.reforecastExpiringRenewalRatiosMonthly
                    .slice(property.reforecastStartMonthIndex, 12)
                    .map(v => +((v * 100).toFixed(1)))
                ),
            counts: expiringCountsStat.reforecastExpiringRenewalsMonthly,
            averageValue: +((expiringCountsStat.reforecastExpiringRenewalRatiosMonthly.average() * 100).toFixed(1)),
            averageCount: +((expiringCountsStat.reforecastExpiringRenewalsMonthly.average()).toFixed(1)),
            totalValue: +((expiringCountsStat.reforecastExpiringRenewalRatiosMonthly.average() * 100).toFixed(1)),
            totalCount: +((expiringCountsStat.reforecastExpiringRenewalsMonthly.sum()).toFixed(1))
        },
        {
            year: property.budgetYear,
            versionType: VersionType.Budget,
            name: `${propertyDisplayBudgetYear(property) % 100} BUDGET`,
            color: COLORS.PRIMARY_400,
            type: "line",
            values: expiringCountsStat.budgetExpiringRenewalRatiosMonthly.map(v => +((v * 100).toFixed(1))),
            counts: expiringCountsStat.budgetExpiringRenewalsMonthly,
            averageValue: +((expiringCountsStat.budgetExpiringRenewalRatiosMonthly.average() * 100).toFixed(1)),
            averageCount: +((expiringCountsStat.budgetExpiringRenewalsMonthly.average()).toFixed(1)),
            totalValue: +((expiringCountsStat.budgetExpiringRenewalRatiosMonthly.average() * 100).toFixed(1)),
            totalCount: +((expiringCountsStat.budgetExpiringRenewalsMonthly.sum()).toFixed(1)),
        },
    ];
    const summary = {
        comparisonItemOne: {
            label: `${property.reforecastYear % 100} RFCST`,
            value: expiringCountsStat.reforecastAvgExpiringRenewalRatio === null ? "-" : formatterPercentAutoSign.format(expiringCountsStat.reforecastAvgExpiringRenewalRatio)
        },
        comparisonItemTwo: {
            label: `${propertyDisplayBudgetYear(property) % 100} BDGT`,
            value: expiringCountsStat.budgetAvgExpiringRenewalRatio === null ? "-" : formatterPercentAutoSign.format(expiringCountsStat.budgetAvgExpiringRenewalRatio)
        },
        variancePercent: {
            label: "% Change",
            value: expiringCountsStat.reforecastAvgExpiringRenewalRatio === null
                    && expiringCountsStat.budgetAvgExpiringRenewalRatio === null
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    ? "-" : formatterPercentAutoSign.format(expiringCountsStat.budgetAvgExpiringRenewalRatio! - expiringCountsStat.reforecastAvgExpiringRenewalRatio!)
        },
        varianceAmount: {
            label: "# Change",
            value: varianceCounts.average().toFixed(1)
        }
    };
    const tableData = getHotTableDataFromParsedSeriesData(
        {varianceCounts, chartSeriesData},
        summary,
        "percent",
        getMonths(property)
    );
    return {
        dataType: "percent",
        varianceCounts: varianceCounts,
        chartSeriesData: chartSeriesData,
        summary: summary,
        tableData
    };
}

export function buildGraphSeriesDataFinancialMetric(financialMetricStat: TSummaryDataFinancialMetric, property: Property): ExecutiveSummaryGraphData {
    const chartSeriesData = [
        {
            year: property.reforecastYear,
            versionType: VersionType.Actuals,
            name: `${property.reforecastYear % 100} REFORECAST - ACTUAL`,
            color: COLORS.LAVENDER_400,
            type: "bar",
            values: financialMetricStat.reforecastMonthly
                .slice(0, property.reforecastStartMonthIndex)
                .map(v => +(v.toFixed()))
                .concat(new Array(12 - property.reforecastStartMonthIndex).fill(null)),
            counts: [],
            averageValue: +((financialMetricStat.reforecastMonthly.average()).toFixed()),
            averageCount: +((financialMetricStat.reforecastMonthly.average()).toFixed()),
            totalValue: +((financialMetricStat.reforecastTotal).toFixed()),
            totalCount: +((financialMetricStat.reforecastTotal).toFixed()),
        },
        {
            year: property.reforecastYear,
            versionType: VersionType.Reforecast,
            name: `${property.reforecastYear % 100} REFORECAST - FORECAST`,
            color: COLORS.LAVENDER_200,
            type: "bar",
            values: new Array(property.reforecastStartMonthIndex).fill(null)
                .concat(financialMetricStat.reforecastMonthly
                    .slice(property.reforecastStartMonthIndex, 12)
                    .map(v => +(v.toFixed()))
                ),
            counts: [],
            averageValue: +((financialMetricStat.reforecastMonthly.average()).toFixed()),
            averageCount: +((financialMetricStat.reforecastMonthly.average()).toFixed()),
            totalValue: +((financialMetricStat.reforecastTotal).toFixed()),
            totalCount: +((financialMetricStat.reforecastTotal).toFixed()),
        },
        {
            year: property.budgetYear,
            versionType: VersionType.Budget,
            name: `${propertyDisplayBudgetYear(property) % 100} BUDGET`,
            color: COLORS.PRIMARY_400,
            type: "line",
            values: financialMetricStat.budgetMonthly.map(v => +(v.toFixed())),
            counts: [],
            averageValue: +((financialMetricStat.budgetMonthly.average()).toFixed()),
            averageCount: +((financialMetricStat.budgetMonthly.average()).toFixed()),
            totalValue: +((financialMetricStat.budgetTotal).toFixed()),
            totalCount: +((financialMetricStat.budgetTotal).toFixed()),
        },
    ];
    const summary = {
        comparisonItemOne: {
            label: `${property.reforecastYear % 100} RFCST`,
            value: formatterDollarUSNoDecimal.format(financialMetricStat.reforecastTotal)
        },
        comparisonItemTwo: {
            label: `${propertyDisplayBudgetYear(property) % 100} BDGT`,
            value: formatterDollarUSNoDecimal.format(financialMetricStat.budgetTotal)
        },
        variancePercent: {
            label: "% Change",
            value: financialMetricStat.variancePercent ? formatterPercentAutoSign.format(financialMetricStat.variancePercent) : "-"
        },
        varianceAmount: {
            label: "$ Change",
            value: financialMetricStat.varianceAmount ? formatterDollarUSNoDecimal.format(financialMetricStat.varianceAmount) : "-"
        }
    };
    const tableData = getHotTableDataFromParsedSeriesData(
        {varianceCounts: [], chartSeriesData},
        summary,
        "dollar",
        getMonths(property)
    );
    return {
        dataType: "dollar",
        varianceCounts: [],
        chartSeriesData: chartSeriesData,
        summary: summary,
        tableData
    };
}
